import type { Thing, WithContext } from 'schema-dts';
import type { Question } from '@/entities/question';
import { tp } from '@/shared/lib/typograf';
import { absoluteFromHost } from '@/shared/lib/utils/absolute-url';

export const FAQ_PAGE_MIN_QA_PAIRS = 2;

function stripHtml(html: string): string {
    return html
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

export function filterFaqQuestionsVisibleInFaqSection(questions: Question[]): Question[] {
    return questions.filter((q) => Boolean(q.answer?.trim()));
}

export function resolveFaqPageAbsoluteUrl(pagePath: string): string {
    const host = (process.env.NEXT_PUBLIC_HOST || '').trim();
    const norm = pagePath.startsWith('/') ? pagePath : `/${pagePath}`;
    const withSlash = norm.endsWith('/') ? norm : `${norm}/`;
    return absoluteFromHost(host, withSlash) || withSlash;
}

export type FaqPageJsonLdPageFields = {
    name?: string;
    subtitleHtml?: string;
};

export function generateFaqPageJsonLd(
    questions: Question[],
    pageUrl: string,
    page?: FaqPageJsonLdPageFields,
): WithContext<Thing> | null {
    const visible = filterFaqQuestionsVisibleInFaqSection(questions);
    if (visible.length < FAQ_PAGE_MIN_QA_PAIRS) return null;

    const mainEntity = visible
        .map((q) => {
            const name = stripHtml(tp(q.question || '')).trim();
            const text = stripHtml(tp(q.answer || '')).trim();
            if (!name || !text) return null;
            return {
                '@type': 'Question' as const,
                name,
                acceptedAnswer: {
                    '@type': 'Answer' as const,
                    text,
                },
            };
        })
        .filter(Boolean) as {
        '@type': 'Question';
        name: string;
        acceptedAnswer: { '@type': 'Answer'; text: string };
    }[];

    if (mainEntity.length < FAQ_PAGE_MIN_QA_PAIRS) return null;

    const description = page?.subtitleHtml ? stripHtml(page.subtitleHtml) || page.subtitleHtml : undefined;

    const baseUrl = pageUrl.replace(/#.*$/, '');

    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        '@id': `${baseUrl}#faq`,
        url: pageUrl,
        ...(page?.name ? { name: page.name } : {}),
        ...(description ? { description } : {}),
        mainEntity,
    } as WithContext<Thing>;
}
